<template>
  <v-container fluid>
    <div id="content">

      <section class="d-flex">
        <BreadCrumb :items="$route.matched"></BreadCrumb>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click.stop="dialog = true" depressed>新しい{{ $route.name }}を作成</v-btn>
      </section>

      <v-card flat>
        <v-card-title>
          <v-row>
            <v-col cols="6" md="3">
              <v-text-field
                v-model="filterKeyword.group"
                label="施設グループ"
                outlined clearable hide-details="auto"></v-text-field>
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field
                v-model="filterKeyword.name"
                label="施設名"
                outlined clearable hide-details="auto"></v-text-field>
            </v-col>
          </v-row>
        </v-card-title>
        <v-data-table
          :headers="tableHeaders"
          :items="filterItems"
          :sort-by="['name']"
          :sort-desc="[true]"
          :loading="loading"
          group-by="group"
          @click:row="onRowClick">
          <template v-slot:group.header="{ group, headers, isOpen, toggle }">
            <td>
              {{ group }}
              <v-btn icon x-small @click.stop="toggle">
                <v-icon>{{ isOpen ? 'fas fa-chevron-circle-down' : 'fas fa-chevron-circle-up' }}</v-icon>
              </v-btn>
            </td>
            <td :colspan="headers.length -1"></td>
          </template>
          <template v-slot:item.enableWeek="{ item }">
            {{ getWeekList(item.enableWeek) }}
          </template>
          <template v-slot:item.enableTime="{ item }">
            {{ getTimeList(item.enableTime) }}
          </template>
          <template v-slot:item.enableFewMonth="{ item }">
            <template v-if="item.enableFewMonth">{{ item.enableFewMonth }}ヵ月先</template>
          </template>
          <template v-slot:item.publish="{ item }">
            {{ item.publish ? '公開' : '非公開' }}
          </template>
        </v-data-table>
      </v-card>

      <!-- 編集ダイアログ -->
      <v-navigation-drawer v-model="dialog" fixed temporary right stateless width="600" id="dialog" class="edit-dialog">
        <v-card flat class="drawer-card">
          <v-card-title>
            <div class="title">{{ $route.name }}を{{ this.editedIndex === -1 ? '追加' : '編集' }}</div>
          </v-card-title>
          <v-card-text>
            <v-form v-model="valid">
              <v-row no-gutters>
                <v-col cols="12" class="pa-2">
                  <v-combobox
                    ref="group"
                    label="施設グループ"
                    v-model="editedItem.group"
                    :items="groups"
                    append-icon="fas fa-list-ol"
                    @click.stop="openSelected('group', true)"
                    readonly outlined hide-details="auto">
                  </v-combobox>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <v-text-field
                    ref="name"
                    label="施設名"
                    v-model="editedItem.name"
                    :rules="[rules.required]"
                    class="required"
                    outlined hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="6" class="pa-2">
                  <v-combobox
                    ref="type"
                    label="予約タイプ"
                    v-model="editedItem.type"
                    :items="types"
                    :rules="[rules.required]"
                    class="required"
                    append-icon="fas fa-list-ol"
                    @click.stop="openSelected('type')"
                    readonly outlined hide-details="auto">
                  </v-combobox>
                </v-col>
                <v-col cols="6" class="pa-2" v-if="editedItem.type == '時間'">
                  <v-select
                    ref="minMinutes"
                    label="最小予約単位"
                    v-model="editedItem.minMinutes"
                    :items="minutes"
                    :rules="[rules.required]"
                    class="required"
                    outlined hide-details="auto">
                  </v-select>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <v-subheader>予約可能曜日</v-subheader>
                  <ul class="list-inline week-checkbox">
                    <li v-for="day of weeks" :key="day.value">
                       <v-checkbox
                        v-model="enableWeek"
                        :label="day.text"
                        :value="day.value">
                      </v-checkbox>
                    </li>
                  </ul>
                </v-col>
                <v-col cols="12" class="pa-2" v-if="editedItem.type == '時間'">
                  <v-subheader>予約可能時間</v-subheader>
                  <v-range-slider
                    v-model="enableTime"
                    min="0"
                    max="1440"
                    :step="editedItem.minMinutes"
                    thumb-size="40"
                    thumb-label="always">
                    <template v-slot:thumb-label="{ value }">
                      {{ Math.floor(value / 60) + ':' + zeroPadding(value % 60, 2) }}
                    </template>
                  </v-range-slider>
                </v-col>
                <v-col cols="6" class="pa-2">
                  <v-text-field
                    ref="enableFewMonth"
                    label="予約可能期間"
                    type="number"
                    v-model="editedItem.enableFewMonth"
                    :rules="[rules.required]"
                    class="required"
                    suffix="ヵ月先"
                    outlined hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <v-subheader>公開設定</v-subheader>
                  <toggle-button
                    v-model="editedItem.publish"
                    :labels="{checked: '公開', unchecked: '非公開'}"
                    color="#0277bd"
                    :value="editedItem.publish"
                    :sync="true"
                    :width="90" :height="30" :font-size="14">
                  </toggle-button>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click.stop="save" :loading="saving" :disabled="!valid" text x-large>
              <v-icon left>fas fa-save</v-icon>{{ this.editedIndex === -1 ? '登録' : '更新' }}
            </v-btn>
            <v-btn color="error" @click.stop="remove" :loading="saving" v-if="editedIndex > -1" text x-large>
              <v-icon left>fas fa-trash-alt</v-icon>削除
            </v-btn>
            <v-btn @click="dialog = false" text x-large>
              <v-icon left>fas fa-times</v-icon>閉じる
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-navigation-drawer>

    </div>
  </v-container>
</template>

<script>
import { API, graphqlOperation } from 'aws-amplify'
import { createFacility, updateFacility, deleteFacility } from '../graphql/mutations'
import { listFacilities } from '../graphql/queries'
import { onCreateFacility, onUpdateFacility, onDeleteFacility } from '../graphql/subscriptions'

export default {
  data() {
    return {
      loading: true,
      saving: false,
      dialog: false,
      valid: false,
      tableHeaders: [
        { text: '施設名', align: 'start', value: 'name' },
        { text: '予約タイプ', align: 'start', value: 'type' },
        { text: '予約可能曜日', align: 'start', value: 'enableWeek' },
        { text: '予約可能時間', align: 'start', value: 'enableTime' },
        { text: '予約可能期間', align: 'start', value: 'enableFewMonth' },
        { text: '公開設定', align: 'start', value: 'publish' },
      ],
      desserts: [],
      editedIndex: -1,
      editedItem: {},
      types: ['時間'],
      minutes: [10, 15, 30],
      weeks: [
        { text: '日', value: '0' },
        { text: '月', value: '1' },
        { text: '火', value: '2' },
        { text: '水', value: '3' },
        { text: '木', value: '4' },
        { text: '金', value: '5' },
        { text: '土', value: '6' },
      ],
      enableWeek: [],
      enableTime: [540, 1080],
      filterKeyword: {},
    }
  },
  prop: {},
  mounted: async function() {
    //一覧 query
    await this.getFacilities()
    //作成時 subscription
    await API.graphql(graphqlOperation(onCreateFacility)).subscribe({
      next: (e) => {
        const data = e.value.data.onCreateFacility
        this.desserts.push(data)
      }
    })
    //更新時 subscription
    await API.graphql(graphqlOperation(onUpdateFacility)).subscribe({
      next: (e) => {
        const data = e.value.data.onUpdateFacility
        this.desserts.find(origin => {
          if (origin.id === data.id) {
            origin = Object.assign(origin, data)
          }
        })
      }
    })
    //削除時 subscription
    await API.graphql(graphqlOperation(onDeleteFacility)).subscribe({
      next: (e) => {
        const data = e.value.data.onDeleteFacility
        this.desserts = this.desserts.filter(origin => origin.id !== data.id)
      }
    })
    this.loading = false
  },
  beforeDestroy() {},
  created() {
    if (!this.isAllowed(this.$route.name)) this.$router.push('/')
  },
  computed: {
    filterItems() {
      const map = []
      for (const item of this.desserts) {
        //施設グループ
        if (this.filterKeyword.group && (!item.group || item.group.indexOf(this.filterKeyword.group) < 0)) continue
        //施設名
        if (this.filterKeyword.name && (!item.name || item.name.indexOf(this.filterKeyword.name) < 0)) continue
        map.push(item)
      }
      return map
    },
    groups() {
      const map = []
      this.desserts.forEach(e => {
        if (e.group && !map.find(m => m === e.group)) map.push(e.group)
      })
      return map
    },
    form() {
      return {
        id: this.editedItem.id || null,
        group: this.editedItem.group || '未分類',
        name: this.editedItem.name || null,
        type: this.editedItem.type || null,
        minMinutes: this.editedItem.type == '時間' ? this.editedItem.minMinutes : null,
        enableWeek: this.enableWeek.map(e => parseInt(e)).sort(),
        enableTime: this.editedItem.type == '時間' ? this.enableTime.map(e => parseInt(e)) : null,
        enableFewMonth: this.editedItem.enableFewMonth || null,
        publish: this.editedItem.publish,
      }
    },
  },
  watch: {
    dialog(val) {
      val || this.close()
    },
  },
  methods: {
    //初期化
    async init() {

    },
    //行選択
    onRowClick(params) {
      this.editedIndex = this.desserts.findIndex(e => e.id === params.id)
      this.editedItem = Object.assign({}, params)
      this.enableWeek = this.editedItem.enableWeek ? this.editedItem.enableWeek.map(e => String(e)) : []
      this.enableTime = this.editedItem.enableTime ? this.editedItem.enableTime.map(e => e) : []
      this.dialog = true
    },
    //保存
    async save() {
      try {
        this.saving = true
        const data = Object.assign({}, this.form)
        //編集
        if (this.editedIndex >= 0) {
          await API.graphql(graphqlOperation(updateFacility, {input: data}))
          this.log({ type: this.$route.name, text: '[' + data.name + '] を更新しました' })
        //新規
        } else {
          delete data.id
          await API.graphql(graphqlOperation(createFacility, {input: data}))
          this.log({ type: this.$route.name, text: '[' + data.name + '] を追加しました' })
        }
        this.dialog = false

      } catch (error) {
        this.myAlert(error.message)
        this.saving = false
      }
    },
    //削除
    async remove() {
      try {
        if (confirm(this.editedItem.name + 'を削除しますか？')) {
          //削除
          await API.graphql(graphqlOperation(deleteFacility, {
            input: { id: this.editedItem.id }
          }))
          this.log({ type: this.$route.name, text: '[' + this.editedItem.name + '] を削除しました' })
          this.myAlert(this.editedItem.name + 'を削除しました', 'success')
          this.dialog = false
        }
      } catch (error) {
        this.myAlert(error)
      }
    },
    //ダイアログclose
    close() {
      this.saving = false
      setTimeout(() => {
        this.resetForm()
        this.editedIndex = -1
      }, 300)
    },
    //フォームreset
    resetForm() {
      Object.keys(this.$refs).forEach(f => {
        if (this.$refs[f]) this.$refs[f].reset()
      })
      this.editedItem.id = null
      this.enableWeek = []
      this.enableTime = [540, 1080]
    },
    //予約可能曜日の取得
    getWeekList(weeks) {
      const map = []
      if (weeks) {
        for (const week of weeks) {
          const day = this.weeks.find(e => parseInt(e.value) == week)
          if (day) map.push(day.text)
        }
      }
      return map.join(' / ')
    },
    //予約可能時間の取得
    getTimeList(times) {
      if (times) {
        let from = times[0] || 540
        let to = times[1] || 1080
        from = Math.floor(from / 60) + ':' + this.zeroPadding(from % 60, 2)
        to = Math.floor(to / 60) + ':' + this.zeroPadding(to % 60, 2)
        return from + ' ～ ' + to

      } else {
        return
      }
    },
    //施設の取得
    async getFacilities(nextToken = null) {
      const query = await API.graphql(graphqlOperation(listFacilities, { limit: 500, nextToken: nextToken }))
      for (const data of query.data.listFacilities.items) {
        this.desserts.push(data)
      }
      if (query.data.listFacilities.nextToken) this.getFacilities(query.data.listFacilities.nextToken)
    },
  },
}
</script>

<style lang='scss' scoped>
.week-checkbox {
  li {
    margin-right: 20px;
  }
}
</style>
